// Implements: F-UIX-018, F-OBS-011
/**
 * The client of the org admin's audit list (UIX.7): `GET /organizations/{org}/audit`.
 *
 * The address names only the organization on screen, its id escaped. A filter left empty is not
 * sent (the server reads a blank value as no filter, and its defaults cover the last 366 days).
 * The server decides access before it reads any id or filter and answers every refused reader
 * with the `404` of a path that does not exist; a malformed filter is a `422` with its message.
 */

import { API_BASE } from "../../../shared/api/apiBase";
import { httpRequest } from "../../../shared/api/httpClient";
import type { AuditAction, AuditPageView } from "../../../types/audit";

/** One read's filters: at most one action, the server filters by one token at a time. */
export interface AuditQuery {
  readonly from: string;
  readonly to: string;
  readonly action: AuditAction | undefined;
  readonly teamId: string;
  readonly mockupId: string;
}

export function auditPath(organizationId: string, query: AuditQuery, cursor: string | null): string {
  const params = new URLSearchParams();
  const add = (name: string, value: string | undefined | null): void => {
    if (value !== undefined && value !== null && value !== "") params.set(name, value);
  };
  add("from", query.from);
  add("to", query.to);
  add("action", query.action);
  add("team_id", query.teamId);
  add("mockup_id", query.mockupId);
  add("cursor", cursor);
  const search = params.toString();
  return `${API_BASE}/organizations/${encodeURIComponent(organizationId)}/audit${search === "" ? "" : `?${search}`}`;
}

export async function fetchAuditPage(
  organizationId: string,
  query: AuditQuery,
  cursor: string | null,
  signal?: AbortSignal,
): Promise<AuditPageView> {
  const path = auditPath(organizationId, query, cursor);
  const body = await httpRequest<AuditPageView>(path, { signal });
  if (body === undefined) throw new Error(`empty answer from ${path}`);
  return body;
}
